import { DataFrame } from '@lib/mobx/dataframe';
import { replaceEmojis } from '@lib/utils/emoji';
import { userStore } from '@stores/User';
import { Media } from '@typings/Media';
import { MediaSearchFilters } from '@typings/MediaFilters';
import { action, computed, makeObservable, observable } from 'mobx';
import { debounce } from 'throttle-debounce';
import { LibraryStore } from './Library';

export type SearchResultItem = {
  id: string;
  score: number;
  mediaId: string;
  startTime: number;
  endTime: number;
  matchType?: 'chunk' | 'exact' | 'semantic' | 'fuzzy';
  chunkId?: string;
  text?: string;
  speaker?: string;
};

export type SearchResult = {
  results: SearchResultItem[];
  media: Record<string, Media>;
};

type TextSearch = {
  text: string;
};
type ImageSearch = {
  image: string;
};
type ClipSearch = {
  clipId: string;
};

export type SegmentType = 'visual' | 'transcript' | 'unified';
export type SearchRequest = {
  filters?: MediaSearchFilters;
} & (
  | (TextSearch & Partial<ImageSearch & ClipSearch>)
  | (ImageSearch & Partial<TextSearch & ClipSearch>)
  | (ClipSearch & Partial<TextSearch & ImageSearch>)
);

export type LibrarySearchRequestBase = {
  filters?: MediaSearchFilters;
  text?: string;
};

export type LibraryVisualSearchRequest = LibrarySearchRequestBase & {
  searchType: 'visual';
  image?: string;
  clipId?: string;
};

export type LibraryTranscriptSearchRequest = LibrarySearchRequestBase & {
  searchType: 'transcript';
};

export type LibraryUnifiedSearchRequest = LibrarySearchRequestBase & {
  searchType: 'unified';
  visual?: {
    image?: string;
    clipId?: string;
  };
};

export class LibrarySearchStore extends DataFrame<SearchResult, SearchResult> {
  @observable
  request: SearchRequest | null = null;

  @observable
  resultType: SegmentType = 'visual';

  @observable
  limit = 100;
  @observable
  skip = 0;

  @observable
  mediaId: string | null = null;

  @computed
  get hasSearchTerms() {
    return !!this.request;
  }

  /**
   * Identity of the question currently being asked. Everything the fetch varies on.
   */
  @computed
  get requestKey(): string {
    return JSON.stringify({
      request: this.request,
      resultType: this.resultType,
      mediaId: this.mediaId,
      limit: this.limit,
      skip: this.skip,
    });
  }

  /** The question the data in hand actually answers. */
  @observable
  private settledRequestKey: string | null = null;

  @action
  private markSettled(key: string) {
    this.settledRequestKey = key;
  }

  /**
   * Whether `data` (or `error`) answers the request being asked *right now*, as
   * opposed to some earlier one.
   *
   * `!!data` cannot answer this and must not be used for it. Every store holds a
   * truthy `{results: [], media: {}}` within 400ms of being created: the request
   * reaction below is `fireImmediately: true`, so `fetch()` runs once with no search
   * terms and returns that empty result. So "data exists" is true for the entire
   * session from boot, and a caller using it to mean "the search has run" gets a
   * green light during the 400ms debounce after every keystroke — long enough for a
   * zero-result state to render "No moments match “d”" over the top of a search that
   * hasn't started.
   *
   * Errors are keyed too, and must be. `internalFetch` does clear `error` before
   * each attempt — but "before each attempt" is 400ms away, because the fetch behind
   * a request change is debounced. Treating any non-null error as settling the
   * *current* request meant that after a failed search, editing the query reported
   * `search-failed` for a query that had not been sent, and fired a zero-result
   * analytics event for it — the exact phantom this gate exists to stop.
   */
  @computed
  get isSettledForCurrentRequest(): boolean {
    if (this.fetching) return false;
    return this.settledRequestKey === this.requestKey;
  }

  constructor(readonly library: LibraryStore) {
    super();

    const debouncedUpdate = debounce(400, () => this.update(), {});

    makeObservable(this);

    // react to the object being replaced
    this.reaction(
      // crude implementaiton to detect changes in the whole object
      // deepObserve doesn't work because we replace the object with a new one
      () => JSON.stringify(this.request),
      debouncedUpdate,
      // fire immediately is necessary for scenarios where we set the request
      // as soon as the class gets created (this should be needed, i don't know why it's not)
      { fireImmediately: true },
    );

    this.reaction(
      () => this.resultType,
      () => this.update(),
    );
    this.reaction(() => this.mediaId, debouncedUpdate);

    this.reaction(() => this.limit, debouncedUpdate);
    this.reaction(() => this.skip, debouncedUpdate);
  }

  @action
  clearSearch() {
    this.request = null;
    this.mediaId = null;
    this.skip = 0;
    this.limit = 100;
  }

  @action
  setSearchRequest(
    request: SearchRequest | null,
    resultType: SegmentType,
    mediaId: string | null = null,
  ) {
    if (request === null) {
      this.clearSearch();
    } else {
      this.request = Object.assign(this.request ?? {}, request);
    }

    this.resultType = resultType;
    this.mediaId = mediaId;
  }

  protected async fetch(abortSignal: AbortSignal): Promise<SearchResult> {
    // Captured before the await, not after: by the time this resolves the request may
    // already have moved on (a keystroke inside the debounce window changes
    // `requestKey` without starting a new fetch), and stamping the *current* key
    // would claim this data answers a question it never saw.
    const askedKey = this.requestKey;
    try {
      return await this.fetchForRequest(abortSignal);
    } finally {
      // `finally`, so a failure stamps too — an error has to settle the request it
      // belongs to, or `isSettledForCurrentRequest` can never distinguish a fresh
      // failure from a stale one. Stamping on abort is harmless: an abort means a
      // newer fetch has started, so `fetching` is true and the getter says false
      // regardless, and that fetch stamps its own key when it lands.
      this.markSettled(askedKey);
    }
  }

  private async fetchForRequest(abortSignal: AbortSignal): Promise<SearchResult> {
    if (!this.hasSearchTerms) {
      return { results: [], media: {} };
    }

    if (this.resultType === 'visual') {
      return this.fetchVisual(abortSignal);
    }

    if (this.resultType === 'unified') {
      return this.fetchUnified(abortSignal);
    }

    return this.fetchTranscript(abortSignal);
  }

  protected async fetchVisual(abortSignal: AbortSignal): Promise<SearchResult> {
    const body = {
      ...this.request,
      mediaIds: this.mediaId ? [this.mediaId] : undefined,
      text: this.request?.text ? await replaceEmojis(this.request.text.trim()) : undefined,
      image: this.request?.image?.startsWith('data:image/')
        ? this.request.image.split(',')[1]
        : undefined,
    };

    return userStore.apiWithAuth(
      `/library/${this.library.libraryId}/search/visual?limit=${this.limit}&offset=${this.skip}`,
      {
        method: 'POST',
        body,
        signal: abortSignal,
      },
    );
  }

  protected async fetchTranscript(abortSignal: AbortSignal): Promise<SearchResult> {
    if (!this.request?.text) return { results: [], media: {} };

    const body = {
      ...this.request,
      mediaIds: this.mediaId ? [this.mediaId] : undefined,
      text: this.request?.text ? await replaceEmojis(this.request.text.trim()) : undefined,
      image: undefined,
      sample: undefined,
    };

    return userStore.apiWithAuth(
      `/library/${this.library.libraryId}/search/transcript?limit=${this.limit}&threshold=0.0`,
      {
        method: 'POST',
        body,
        signal: abortSignal,
      },
    );
  }

  protected async fetchUnified(abortSignal: AbortSignal): Promise<SearchResult> {
    // Request has image and clipId, we need to "move" them to visual object in body.
    const request = {
      ...this.request,
      image: undefined,
      clipId: undefined,
    };

    const body = {
      ...request,
      mediaIds: this.mediaId ? [this.mediaId] : undefined,
      text: this.request?.text ? await replaceEmojis(this.request.text.trim()) : undefined,
      visual: {
        image: this.request?.image?.startsWith('data:image/')
          ? this.request.image.split(',')[1]
          : undefined,
        clipId: this.request?.clipId,
      },
    };

    return userStore.apiWithAuth(
      `/library/${this.library.libraryId}/search/unified?limit=${this.limit}`,
      {
        method: 'POST',
        body,
        signal: abortSignal,
      },
    );
  }
}
